import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '@/lib/api'
import type { Driver, PaginatedDrivers } from '@/types/driver'
import type { DriverFormData } from '@/validators/driver'

export interface ListDriverParams {
  search?: string
  active?: boolean
  page?: number
  perPage?: number
}

const KEYS = {
  list: (params: ListDriverParams) => ['drivers', params] as const,
  detail: (id: string) => ['drivers', id] as const,
}

export function useDrivers(params: ListDriverParams = {}) {
  return useQuery({
    queryKey: KEYS.list(params),
    queryFn: () =>
      api.get<PaginatedDrivers>('/drivers', { params }).then((r) => r.data),
  })
}

export function useDriver(id: string) {
  return useQuery({
    queryKey: KEYS.detail(id),
    queryFn: () => api.get<Driver>(`/drivers/${id}`).then((r) => r.data),
    enabled: !!id,
  })
}

export function useCreateDriver() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: DriverFormData) =>
      api.post<Driver>('/drivers', data).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['drivers'] }),
  })
}

export function useUpdateDriver(id: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: DriverFormData) =>
      api.put<Driver>(`/drivers/${id}`, data).then((r) => r.data),
    onSuccess: (updated) => {
      qc.invalidateQueries({ queryKey: ['drivers'] })
      qc.setQueryData(KEYS.detail(id), updated)
    },
  })
}

export function useDeactivateDriver() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) =>
      api.patch<Driver>(`/drivers/${id}/deactivate`).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['drivers'] }),
  })
}

export function useActivateDriver() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) =>
      api.patch<Driver>(`/drivers/${id}/activate`).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['drivers'] }),
  })
}
